import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Alert, AlertDescription, AlertTitle } from "../../components/ui/alert";
import { Database, Server, Loader2, CheckCircle2, AlertCircle, Clock, BarChart2 } from "lucide-react";
import { useCanadaStats, useUSStats, usePipelineStatus, useFunderOverview } from "../../hooks/useDataIntel";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
} from "recharts";

const COLORS = ["#14b8a6", "#3B82F6", "#F59E0B", "#8B5CF6", "#f97316", "#22c55e", "#ec4899", "#64748b", "#ef4444", "#a3e635"];

export const DataIntelTab = () => {
  const { data: canadaStats, isLoading: canadaLoading, error: canadaError } = useCanadaStats();
  const { data: usStats, isLoading: usLoading, error: usError } = useUSStats();
  const { data: pipelineStatus, isLoading: pipelineLoading } = usePipelineStatus();
  const { data: funderOverview, isLoading: funderLoading, error: funderError } = useFunderOverview();

  return (
    <div className="space-y-6" data-testid="dataintel-tab">
      {/* Funder Landscape */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2">
            <BarChart2 className="h-5 w-5 text-violet-600" />
            Funder Landscape
          </CardTitle>
          <CardDescription>
            {funderOverview
              ? `${funderOverview.total_programs?.toLocaleString()} total funder programs across ${funderOverview.by_source?.length} sources`
              : "Live funder program analytics"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {funderLoading && (
            <div className="flex items-center gap-2 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" />Loading funder data...
            </div>
          )}
          {funderError && (
            <div className="text-sm text-red-600">Error: {funderError.message}</div>
          )}
          {funderOverview && (
            <div className="space-y-6">
              {/* Summary tiles */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {[
                  { label: "Total Programs", value: funderOverview.total_programs?.toLocaleString(), color: "teal" },
                  { label: "Active", value: funderOverview.active_programs?.toLocaleString(), color: "emerald" },
                  { label: "With Restrictions", value: funderOverview.programs_with_restrictions_parsed?.toLocaleString(), color: "amber" },
                  { label: "Sources", value: funderOverview.by_source?.length, color: "blue" },
                ].map((s) => (
                  <div key={s.label} className={`bg-${s.color}-50 border border-${s.color}-200 rounded-lg p-3 text-center`}>
                    <p className={`text-2xl font-bold text-${s.color}-700`}>{s.value}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{s.label}</p>
                  </div>
                ))}
              </div>

              {/* Source Breakdown */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-semibold text-zinc-700 mb-3">Programs by Source</p>
                  <div className="space-y-3">
                    {funderOverview.by_source?.map((s, i) => (
                      <div key={s.source}>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="capitalize text-zinc-700">{s.source}</span>
                          <span className="text-zinc-500">{s.total?.toLocaleString()} programs</span>
                        </div>
                        <div className="flex gap-2 items-center">
                          <div className="flex-1 h-2 bg-zinc-100 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(s.total / funderOverview.total_programs) * 100}%`,
                                backgroundColor: COLORS[i % COLORS.length],
                              }}
                            />
                          </div>
                          <Badge variant="outline" className="text-xs">{s.has_restrictions} restricted</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Deadline Types */}
                <div>
                  <p className="text-sm font-semibold text-zinc-700 mb-3">Deadline Types</p>
                  <div className="space-y-2">
                    {funderOverview.deadline_types?.map((d, i) => (
                      <div key={d.type} className="flex items-center justify-between bg-zinc-50 rounded-lg px-3 py-2">
                        <span className="text-sm capitalize text-zinc-700">{d.type}</span>
                        <div className="flex items-center gap-2">
                          <div className="w-24 h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${(d.count / funderOverview.total_programs) * 100}%`,
                                backgroundColor: COLORS[i % COLORS.length],
                              }}
                            />
                          </div>
                          <span className="text-xs text-zinc-500 w-14 text-right">{d.count?.toLocaleString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Gift Size Distribution */}
              {funderOverview.gift_size_distribution?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-zinc-700 mb-3">Grant Size Distribution (Median Gift)</p>
                  <div className="h-52">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={funderOverview.gift_size_distribution} margin={{ top: 4, right: 4, left: -10, bottom: 4 }}>
                        <XAxis dataKey="size" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ background: "#fff", border: "1px solid #e4e4e7", borderRadius: "8px", fontSize: "12px" }}
                        />
                        <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                          {funderOverview.gift_size_distribution.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}

              {/* Ineligible types */}
              {funderOverview.ineligible_type_counts?.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-zinc-700 mb-3">Most Common Ineligible Project Types</p>
                  <div className="flex flex-wrap gap-2">
                    {funderOverview.ineligible_type_counts.map((t, i) => (
                      <Badge key={t.type} variant="outline" className="text-xs capitalize">
                        {t.type} <span className="ml-1 text-zinc-400">({t.count})</span>
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Database Statistics */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Canada */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Canadian Charity Database
              {canadaLoading ? <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                : canadaError ? <AlertCircle className="h-4 w-4 text-red-500" />
                : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            </CardTitle>
            <CardDescription>PostgreSQL on Azure (20.39.61.12)</CardDescription>
          </CardHeader>
          <CardContent>
            {canadaLoading && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>}
            {canadaError && <div className="text-sm text-red-600">Error: {canadaError.message}</div>}
            {canadaStats && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Database:</span><Badge variant="outline">{canadaStats.database}</Badge></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Years:</span><span className="font-medium">{canadaStats.years}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Total Records:</span><span className="text-2xl font-bold text-teal-600">{canadaStats.total_records?.toLocaleString()}</span></div>
                {canadaStats.tables && (
                  <div className="border-t pt-3 space-y-1.5">
                    <p className="text-xs font-medium text-zinc-400 uppercase">Tables</p>
                    {Object.entries(canadaStats.tables).map(([table, count]) => (
                      <div key={table} className="flex justify-between text-sm">
                        <span className="text-zinc-500">{table}</span>
                        <span className="font-medium">{count?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* US */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              US Nonprofit Database
              {usLoading ? <Loader2 className="h-4 w-4 animate-spin text-zinc-400" />
                : usError ? <AlertCircle className="h-4 w-4 text-red-500" />
                : <CheckCircle2 className="h-4 w-4 text-emerald-500" />}
            </CardTitle>
            <CardDescription>PostgreSQL on Vultr (172.190.60.207)</CardDescription>
          </CardHeader>
          <CardContent>
            {usLoading && <div className="flex items-center gap-2 text-sm text-zinc-500"><Loader2 className="h-4 w-4 animate-spin" />Loading...</div>}
            {usError && <div className="text-sm text-red-600">Error: {usError.message}</div>}
            {usStats && (
              <div className="space-y-3">
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Database:</span><Badge variant="outline">{usStats.database}</Badge></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Years:</span><span className="font-medium">{usStats.years}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-500">Total Records:</span><span className="text-2xl font-bold text-cyan-600">{usStats.total_records?.toLocaleString()}</span></div>
                {usStats.tables && (
                  <div className="border-t pt-3 space-y-1.5">
                    <p className="text-xs font-medium text-zinc-400 uppercase">Tables</p>
                    {Object.entries(usStats.tables).map(([table, count]) => (
                      <div key={table} className="flex justify-between text-sm">
                        <span className="text-zinc-500">{table}</span>
                        <span className="font-medium">{count?.toLocaleString()}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Pipeline Status */}
      {pipelineStatus && (
        <Card>
          <CardHeader>
            <CardTitle>Data Pipeline Status</CardTitle>
            <CardDescription>Real-time status of all data processing stages</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(pipelineStatus).map(([phase, details]) => (
                <div key={phase} className="border rounded-lg p-4">
                  <h4 className="font-medium text-sm mb-3 capitalize">{phase.replace(/_/g, " ")}</h4>
                  <div className="space-y-2">
                    {Object.entries(details).map(([key, value]) => (
                      <div key={key} className="flex items-start gap-2 text-xs">
                        <span className="mt-0.5">
                          {String(value).includes("✅") && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                          {String(value).includes("⚠️") && <Clock className="h-3 w-3 text-amber-500" />}
                          {String(value).includes("🚧") && <AlertCircle className="h-3 w-3 text-blue-500" />}
                          {String(value).includes("❌") && <AlertCircle className="h-3 w-3 text-red-500" />}
                        </span>
                        <div>
                          <p className="text-zinc-600 font-medium">{key.replace(/_/g, " ")}</p>
                          <p className="text-zinc-500">{value}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DataIntelTab;
